import React, { useCallback, useState } from "react";
import { Upload, File, X } from "lucide-react";
import { InputDefinition } from "../../types/pipeline";

interface DynamicWorkflowInputsProps {
  inputs: InputDefinition[];
  values: Record<string, any>;
  errors: Record<string, string>;
  onChange: (name: string, value: any) => void;
  onError: (name: string, error: string) => void;
}

interface FileMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
  content?: string; // Content is present for newly uploaded files, absent for saved files
}

export const DynamicWorkflowInputs: React.FC<DynamicWorkflowInputsProps> = ({
  inputs,
  values,
  errors,
  onChange,
  onError,
}) => {
  const [dragOver, setDragOver] = useState<string | null>(null);

  const handleTextChange = useCallback(
    (name: string, value: string) => {
      onChange(name, value);
      if (errors[name]) {
        onError(name, "");
      }
    },
    [onChange, onError, errors]
  );

  const validateFile = useCallback((file: File): string | null => {
    const allowedExtensions = [".txt", ".md", ".spec", ".pdf", ".doc", ".docx", ".csv", ".json", ".yaml", ".yml"];
    const maxSize = 1024 * 1024; // 1MB

    const extension = "." + file.name.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return `Invalid file type. Allowed: ${allowedExtensions.join(", ")}`;
    }

    if (file.size > maxSize) {
      return "File size must be less than 1MB.";
    }

    return null;
  }, []);

  const handleFileUpload = useCallback(
    async (name: string, files: FileList) => {
      const newFileMetadata: FileMetadata[] = [];
      const fileErrors: string[] = [];

      // Validate all files first
      const validFiles: File[] = [];
      Array.from(files).forEach((file) => {
        const error = validateFile(file);
        if (error) {
          fileErrors.push(`${file.name}: ${error}`);
        } else {
          validFiles.push(file);
        }
      });

      if (fileErrors.length > 0) {
        onError(name, fileErrors.join(" "));
        return; // Don't add files if there are errors
      } else {
        onError(name, "");
      }

      // Read content for all valid files
      for (const file of validFiles) {
        try {
          const content = await file.text();
          const fileId = `${file.name}-${Date.now()}-${Math.random()}`;
          const metadata: FileMetadata = {
            id: fileId,
            name: file.name,
            size: file.size,
            type: file.type,
            content: content, // Embed content directly
          };
          newFileMetadata.push(metadata);
        } catch (error) {
          console.error(`Failed to read file ${file.name}:`, error);
          onError(name, `Failed to read file ${file.name}`);
          return;
        }
      }

      // Get existing file metadata from values (includes both saved and previously uploaded)
      const existingMetadata: FileMetadata[] = Array.isArray(values[name])
        ? values[name]
        : [];

      // Combine existing and new metadata (all with content for new files)
      const allMetadata = [...existingMetadata, ...newFileMetadata];

      // Update file metadata in values (single source of truth)
      onChange(name, allMetadata);
    },
    [validateFile, onError, onChange, values]
  );

  const handleDrop = useCallback(
    (name: string, e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(null);

      if (e.dataTransfer.files) {
        handleFileUpload(name, e.dataTransfer.files);
      }
    },
    [handleFileUpload]
  );

  const handleDragOver = useCallback((name: string, e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(name);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
  }, []);

  const removeFile = useCallback(
    (inputName: string, fileId: string) => {
      // Get current file metadata from values
      const currentMetadata: FileMetadata[] = Array.isArray(values[inputName])
        ? values[inputName]
        : [];

      // Filter out the file being removed
      const updatedMetadata = currentMetadata.filter((f) => f.id !== fileId);

      // Update values (single source of truth)
      onChange(inputName, updatedMetadata);
    },
    [onChange, values]
  );

  const renderInput = (input: InputDefinition) => {
    // Show asterisk for all required inputs
    const isRequired = input.required;

    // For select inputs, parse the default value if needed
    let defaultValue = input.default_value ?? "";
    if (input.display_type === "select" && defaultValue) {
      const parts = defaultValue.split("|").filter((s) => s.trim());
      defaultValue =
        parts
          .find((p) => p.startsWith("*"))
          ?.replace(/^\*/, "")
          .trim() || "";
    }

    const value = values[input.name] ?? defaultValue;

    // String input types
    if (input.type === "string") {
      if (input.display_type === "text") {
        return (
          <div key={input.name}>
            <label
              htmlFor={`input-${input.name}`}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {input.name} {isRequired && "*"}
            </label>
            {input.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {input.description}
              </p>
            )}
            <input
              id={`input-${input.name}`}
              type="text"
              value={value}
              onChange={(e) => handleTextChange(input.name, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                errors[input.name] ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={input.default_value || `Enter ${input.name}...`}
            />
            {errors[input.name] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors[input.name]}
              </p>
            )}
          </div>
        );
      } else if (input.display_type === "textarea") {
        return (
          <div key={input.name}>
            <label
              htmlFor={`input-${input.name}`}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {input.name} {isRequired && "*"}
            </label>
            {input.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {input.description}
              </p>
            )}
            <textarea
              id={`input-${input.name}`}
              value={value}
              onChange={(e) => handleTextChange(input.name, e.target.value)}
              rows={6}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                errors[input.name] ? "border-red-500" : "border-gray-300"
              }`}
              placeholder={input.default_value || `Enter ${input.name}...`}
            />
            {errors[input.name] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors[input.name]}
              </p>
            )}
          </div>
        );
      } else if (input.display_type === "select") {
        // Parse select options from format: |Option 1|Option 2|*Default|Option 3
        const parseSelectOptions = (formatString: string) => {
          if (!formatString) {
            return { options: [] };
          }
          const parts = formatString.split("|").filter((s) => s.trim());
          const options = parts.map((p) => p.replace(/^\*/, "").trim());
          return { options };
        };

        const { options } = parseSelectOptions(input.default_value || "");

        return (
          <div key={input.name}>
            <label
              htmlFor={`input-${input.name}`}
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {input.name} {isRequired && "*"}
            </label>
            {input.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {input.description}
              </p>
            )}
            <select
              id={`input-${input.name}`}
              value={value}
              onChange={(e) => handleTextChange(input.name, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                errors[input.name] ? "border-red-500" : "border-gray-300"
              }`}
            >
              {!isRequired && <option value="">-- Select --</option>}
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors[input.name] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors[input.name]}
              </p>
            )}
          </div>
        );
      }
    }

    // File/list input type
    if ((input.type === "list" || input.type === "file") && input.display_type === "file") {
      // Get all file metadata from values (single source of truth)
      const allFiles: FileMetadata[] = Array.isArray(values[input.name])
        ? values[input.name]
        : [];
      const totalFileCount = allFiles.length;

      return (
        <div key={input.name}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {input.name} {isRequired && "*"}
          </label>
          {input.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {input.description}
            </p>
          )}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center ${
              dragOver === input.name
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-600"
            }`}
            onDrop={(e) => handleDrop(input.name, e)}
            onDragOver={(e) => handleDragOver(input.name, e)}
            onDragLeave={handleDragLeave}
          >
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Drag and drop files here, or{" "}
              <label className="text-blue-600 hover:text-blue-500 cursor-pointer">
                browse
                <input
                  type="file"
                  multiple
                  accept=".txt,.md,.spec,.pdf,.doc,.docx,.csv,.json,.yaml,.yml"
                  onChange={(e) =>
                    e.target.files &&
                    handleFileUpload(input.name, e.target.files)
                  }
                  className="hidden"
                />
              </label>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Max 1MB per file
            </p>
          </div>

          {errors[input.name] && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              {errors[input.name]}
            </p>
          )}

          {/* All Files List (unified display) */}
          {totalFileCount > 0 && (
            <div className="mt-4 space-y-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Files ({totalFileCount})
              </h4>

              {allFiles.map((fileMetadata: FileMetadata) => {
                // Check if this is a newly uploaded file (has content)
                const isNewFile = !!fileMetadata.content;

                return (
                  <div
                    key={fileMetadata.id}
                    className={`flex items-center justify-between p-2 rounded ${
                      isNewFile
                        ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                        : "bg-gray-100 dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <File
                        className={`h-4 w-4 ${
                          isNewFile ? "text-green-600" : "text-gray-500"
                        }`}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {fileMetadata.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({Math.round(fileMetadata.size / 1024)} KB)
                      </span>
                      {isNewFile && (
                        <span className="text-xs text-green-600 dark:text-green-400">
                          (new)
                        </span>
                      )}
                      {!isNewFile && (
                        <span className="text-xs text-blue-600 dark:text-blue-400">
                          (saved)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(input.name, fileMetadata.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Remove file"
                      aria-label={`Remove ${fileMetadata.name}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
        Workflow Inputs
      </h3>
      {inputs.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No inputs defined for this pipeline.
        </p>
      )}
      {inputs.map((input) => renderInput(input))}
    </div>
  );
};
